/**
 * 语文听写（家长端）：**用**字表的地方 —— 看字表、选词、看会取到哪些词。
 *
 * ## 2026-10-16：这一页从「录入界面」改成「只读 + 取词」
 *
 * 用户的原话（这次改造的依据）：
 *
 * > "这个语文听写录入这个字词组成语……放在某个家长下面，我觉得就不太合适。
 * >  你想，是不是每个家长注册之后，每个家庭那个家长都要录一遍这个字啊？
 * >  那岂不是太浪费时间，而且没有办法资源重用吗？所以我觉得这个工具类应该……
 * >  **不是家长负责维护，而应该是站点负责维护**。站点提供这个工具，
 * >  这工具家长拿到就能用，他不需要录入那些语文书上那些字词。……
 * >  所以我觉得**录入维护这一块应该由系统管理员去做**，应该放到系统管理员那里去。"
 *
 * 于是：
 *   · **录入/导入/改拼音**（21 条维护端点）搬去 `/admin/dictation`（管理员后台）；
 *   · 这一页只保留**用**：按书看字表、浏览字词、选词预览。
 *     编辑与导入**一个入口都不渲染**（不是禁用按钮 —— 本项目明确反对
 *     "点了会失败"的按钮，而且那些端点对家长**根本不存在**）。
 *   · 家长自己的东西（听写布置 / 播放 / 批改 / 错词本 / **录音**）仍然在
 *     `/students/:id/dictation`，那是**这个孩子的**，与站点字表是两件事。
 *
 * ## ⚠️ 内部仍然要挑一个孩子，但**界面上一个字都不提**
 *
 * 「使用类」端点是 `/students/:id/dictation/...`，每个都先过
 * `resolveStudentAccess(app, principal, :id)` —— 那个孩子是**权限锚点**：
 * 只决定"你能不能进这一页"与"选词预览默认哪个年级"，**不**决定字表里有什么
 * （换个孩子看到的是**同一份**字表）。见 `lib/dictationAnchor.ts`。
 *
 * 界面上：横幅只说"这套字表是站点提供的，直接用就行"；
 * **没有**"换个孩子当锚点"的选择器，也**不提**"锚点 / 接口挂在孩子下面"这类实现细节。
 *
 * ## 三态：0 / 空 / 拿不到（这是本项目踩过三次的坑）
 *
 *   · 一个孩子都没有 → 人话空状态（不是报错，也不是"到处是 —"的页面）；
 *   · 站点还没有字表（`textbooks: []`）→ "站点还没有录入字表，请联系管理员"
 *     （由 `DictationWorkspace` 里那条空状态负责）；
 *   · 字段没拿到 → 「—」+ "数据没拿到"，**不是**"一条都没有"。
 */
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { Card, EmptyState, Skeleton } from '../../components/ui/Card';
import { useStudent, useStudents } from '../../lib/queries';
import { resolveDictationAnchor } from '../../lib/dictationAnchor';
import { DictationWorkspace } from '../../components/dictation/DictationWorkspace';

export function ParentDictationPage() {
  /**
   * ⚠️ 这里**只读**查询串，界面上**没有**写它的控件（2026-10 去掉了"换个孩子"选择器）。
   * `?anchor=` 留着是为了将来"从某个孩子的听写布置跳进来"时能把默认年级带过来。
   */
  const [searchParams] = useSearchParams();

  const studentsQuery = useStudents();
  const requestedAnchor = searchParams.get('anchor');
  const anchor = useMemo(
    () => resolveDictationAnchor(studentsQuery.data ?? [], requestedAnchor),
    [studentsQuery.data, requestedAnchor],
  );
  const anchorId = anchor?.id ?? '';
  const anchorReady = anchorId !== '';

  /**
   * 那个孩子的档案单独拉一次：**只为拿 `gradeCode`**（选词预览的默认年级）。
   * ⚠️ 不再用它判 `canEdit` —— 字表现在谁都不能在家长端改（那是管理员的事）。
   */
  const studentQuery = useStudent(anchorId);

  /**
   * 名单还没到 → 先给骨架（此时**还不知道**该用哪个孩子发请求）；
   * 一个孩子都没有 → 空状态。
   *
   * ⚠️ 空状态这条**不能省**：一个孩子账号都没有时，那些端点一个都发不出去。
   *   直接渲染下面的界面会得到一张"到处是 —、按什么按钮都失败"的页，
   *   家长看到的会是"这个功能坏了"，而不是"我还没有孩子账号"。
   * ⚠️ 但**不要**用"锚点/后端接口"这类实现细节解释它 —— 家长只需要知道下一步做什么。
   */
  if (studentsQuery.isLoading || !anchorReady) {
    return (
      <AppShell kind="parent" title="语文听写">
        {studentsQuery.isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <EmptyState
            icon="🧒"
            title="还没有孩子账号"
            description="先在首页添加一个孩子账号，再回这里看字表、给孩子布置听写。"
            action={
              <Link to="/" className="mt-2 text-sm font-medium text-brand-600 hover:underline">
                回首页添加孩子
              </Link>
            }
          />
        )}
      </AppShell>
    );
  }

  return (
    <AppShell kind="parent" title="语文听写">
      <div className="space-y-4">
        {/*
          ---------- 第一屏：这套字表是**站点提供的** ----------
          ⚠️ 这里**只**讲家长用得着的事：
             · 字表是站点维护好的，拿到就能用（家长不需要录任何东西）；
             · 家长能为这个家做的是"给多音字录一段自己的音"（那是家庭专属的），
               但这一步在**孩子那一页的听写里**做，不在这里。
          ⚠️ 不提"权限锚点 / 这一页的地址里没有孩子 / 为了调后端接口"这类实现细节。
        */}
        <Card className="bg-brand-50/60">
          <h2 className="text-base font-semibold text-slate-900">这套字表是站点提供的，直接用就行</h2>
          <p className="mt-1 text-sm text-slate-600">
            课本、课文、生字、词语都由站点统一维护好 —— 你不需要录入任何字词。
            这里可以翻看字表、也看看某次听写会取到哪些词；要布置听写请进孩子那一页。
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="rounded-full bg-white/70 px-2 py-0.5 text-xs font-medium text-slate-600">
              站点维护
            </span>
            <span className="rounded-full bg-white/70 px-2 py-0.5 text-xs font-medium text-slate-600">
              家长只读
            </span>
          </div>
          <p className="mt-3 text-sm text-slate-600">
            多音字读不准时，可以在
            <strong>孩子那一页的「朗读录音」</strong>
            里自己录一段音替换掉机器朗读（录音只在自己家里生效，不会影响别人）。
          </p>
        </Card>

        <DictationWorkspace
          scope={{ kind: 'parent', studentId: anchorId }}
          mode="browse"
          studentGradeCode={studentQuery.data?.gradeCode ?? ''}
        />
      </div>
    </AppShell>
  );
}
